import React, { useCallback, useMemo } from "react";
import { Grid, IconButton, makeStyles, Tooltip, Typography } from "@material-ui/core";
import styles from "../styles";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { GetApp } from "@material-ui/icons";
import { get, over, set } from "@totalsoft/change-tracking-react";
import { DOWNLOAD_CONTRACT_REQUIRED_DOCUMENT_TEMPLATE_QUERY } from "../queries";
import fileSaver from "file-saver";
import { useApolloClient } from "@apollo/client";
import { useMutationWithErrorHandling } from "hooks/errorHandling";
import { convertBase64ToFile } from "utils/fileConverter";
import { useClientQueryWithErrorHandling } from "hooks/errorHandling";
import { UPLOAD_CONTRACT_REQUIRED_DOCUMENT } from "../mutations";
import { REMOVE_REQUIRED_DOCUMENT } from "../../dataCompletion/mutations";
import { DOWNLOAD_DMS_FILE_QUERY } from "features/dataCompletion/queries";
import { gql } from "@apollo/client";
import { remove } from "ramda";
import cx from "classnames";
import UploadDocuments from "features/common/components/UploadDocuments";
import DownloadOrDeleteDocument from "features/common/components/DownloadOrDeleteDocument";
import { addToast } from "components/notifications";
import { quoteSteps } from "features/quote/constants";

const useStyles = makeStyles(styles);

function ContractDocumentComponent({
  contractId,
  contractSiteId,
  leasingContractDocumentLens,
  clientId,
  index,
  quoteId,
  disabled,
}) {
  const { t } = useTranslation();
  const clientQuery = useClientQueryWithErrorHandling();
  const client = useApolloClient();

  const enabledAddFile = useMemo(() => {
    if (
      (leasingContractDocumentLens.files |> get).length < 1 ||
      !(leasingContractDocumentLens.requiredDocTypeId |> get)
    )
      return true;
    return false;
  }, [leasingContractDocumentLens.files, leasingContractDocumentLens.requiredDocTypeId]);

  const styleProps = useMemo(() => ({ disabledDropzone: !enabledAddFile || disabled }), [disabled, enabledAddFile]);
  const classes = useStyles(styleProps);

  const [removeFile] = useMutationWithErrorHandling(REMOVE_REQUIRED_DOCUMENT);

  const handleDeleteFile = useCallback(
    (sharePointUID, index) => {
      removeFile({ variables: { quoteId, sharePointUID } });
      over(leasingContractDocumentLens.files, remove(index, 1));

      const idToDelete = client.cache.identify({
        __typename: "DmsFile",
        sharePointUID: sharePointUID,
      });

      client.cache.evict({ id: idToDelete });
      client.cache.gc();
    },
    [removeFile, client.cache, quoteId, leasingContractDocumentLens.files]
  );

  const handleDownloadFormTemplate = useCallback(async () => {
    const { data } = await clientQuery(DOWNLOAD_CONTRACT_REQUIRED_DOCUMENT_TEMPLATE_QUERY, {
      variables: {
        quoteId,
        contractId,
        contractSiteId,
        guarantorId: leasingContractDocumentLens.guarantorId |> get,
        guarantorName: leasingContractDocumentLens.guarantorName |> get,
        clientId,
        documentTemplateId: leasingContractDocumentLens.documentTemplateId |> get,
        templateContentType: leasingContractDocumentLens.templateContentType |> get,
      },
      fetchPolicy: "no-cache",
    });

    if (!data) return;

    const fileURL = convertBase64ToFile(data.downloadContractRequiredDocumentTemplate.content);
    fileSaver.saveAs(fileURL, data.downloadContractRequiredDocumentTemplate.name);
  }, [clientQuery, quoteId, clientId, contractId, contractSiteId, leasingContractDocumentLens]);

  const handleDownloadFile = useCallback(
    async (sharePointUID, fileName) => {
      const { data } = await clientQuery(DOWNLOAD_DMS_FILE_QUERY, {
        variables: {
          quoteId,
          sharePointUID,
          fileName,
          quoteStep: quoteSteps.contract,
          guarantorId: leasingContractDocumentLens.guarantorId |> get,
        },
        fetchPolicy: "no-cache",
      });

      if (!data) return;

      const fileURL = convertBase64ToFile(data.downloadDmsFile.content);
      fileSaver.saveAs(fileURL, data.downloadDmsFile.name);
    },
    [clientQuery, quoteId, leasingContractDocumentLens.guarantorId]
  );

  const [uploadFile] = useMutationWithErrorHandling(UPLOAD_CONTRACT_REQUIRED_DOCUMENT, {
    onCompleted: (data) => {
      const currentFiles = leasingContractDocumentLens.files |> get;
      const newFiles = [...currentFiles];
      data.uploadContractRequiredDocument.forEach((file) => {
        newFiles.push(file);
      });
      (leasingContractDocumentLens.files |> set)(newFiles);

      client.cache.modify({
        id: client.cache.identify({
          __typename: "RequiredDocument",
          quoteId,
          id: leasingContractDocumentLens.id |> get,
          guarantorId: leasingContractDocumentLens.guarantorId |> get,
        }),
        fields: {
          files(filesRef) {
            data.uploadContractRequiredDocument.forEach((file) => {
              filesRef = [
                ...filesRef,
                client.cache.writeFragment({
                  data: file,
                  fragment: gql`
                    fragment NewFile on DmsFile {
                      sharePointUID
                      fileName
                    }
                  `,
                }),
              ];
            });
            return [...filesRef];
          },
        },
      });
    },
  });

  const handleUpload = useCallback(
    async (files) => {
      if (files.length <= 0) return false;

      const uploadedFiles = files.map((file) => {
        return file.file;
      });

      await uploadFile({
        variables: {
          quoteId,
          contractId,
          contractSiteId,
          clientId,
          guarantorId: leasingContractDocumentLens.guarantorId |> get,
          dmsContentType: leasingContractDocumentLens.requiredDocContentType |> get,
          uploadedFiles,
        },
      });
      addToast(t("ToastNotifications.Success"), "success");
    },
    [
      quoteId,
      uploadFile,
      clientId,
      contractId,
      contractSiteId,
      leasingContractDocumentLens.guarantorId,
      leasingContractDocumentLens.requiredDocContentType,
      t,
    ]
  );

  return (
    <Grid
      container
      alignItems="center"
      className={index === 0 ? classes.firstReqDocBorder : classes.restOfReqDocBorder}
    >
      <Grid item>
        {!(leasingContractDocumentLens.guarantorId |> get) ? (
          <Typography variant="h6" className={classes.reqText}>
            {leasingContractDocumentLens.caption |> get}
          </Typography>
        ) : (
          <Typography variant="h6" className={classes.reqText}>
            {(leasingContractDocumentLens.caption |> get) + " " + (leasingContractDocumentLens.guarantorName |> get)}
          </Typography>
        )}
      </Grid>
      <Grid item>
        {(leasingContractDocumentLens.isMandatory |> get) && (
          <Tooltip title={t("General.MandatoryDocument")} placement="top">
            <Typography variant="h6" className={cx(classes.reqText, classes.reqTextRed)}>
              {"*"}
            </Typography>
          </Tooltip>
        )}
      </Grid>
      <Grid item container xs alignItems="center" justifyContent="flex-end">
        <IconButton disabled={false} onClick={handleDownloadFormTemplate} className={classes.templateDwIconButton}>
          <Tooltip title={t("General.FormTemplates")} placement="top">
            <GetApp color="action" fontSize="small" />
          </Tooltip>
        </IconButton>
        <UploadDocuments disabled={!enabledAddFile || disabled} onUpload={handleUpload} />
      </Grid>
      {(leasingContractDocumentLens.files |> get).map((file, index) => (
        <DownloadOrDeleteDocument
          key={index}
          index={index}
          file={file}
          onDownloadFile={handleDownloadFile}
          onDeleteFile={handleDeleteFile}
          disabled={disabled}
        />
      ))}
    </Grid>
  );
}

ContractDocumentComponent.propTypes = {
  clientId: PropTypes.number.isRequired,
  contractId: PropTypes.number.isRequired,
  contractSiteId: PropTypes.number.isRequired,
  leasingContractDocumentLens: PropTypes.object,
  index: PropTypes.number.isRequired,
  quoteId: PropTypes.number,
  disabled: PropTypes.bool.isRequired,
};

export default ContractDocumentComponent;
